import { useEffect, useState } from "react";
import Button from "../Button/Button";
import Calendar from "../Calendar/Calendar";
import InputAutocomplete from "../InputAutocomplete/InputAutocomplete";
import SearchFilter from "../SearchFilter/SearchFilter";
import {
  IAutocompleteFilterProps,
  IButtonProps,
  ICalendarProps,
  IOption,
  ISearchFilterConfig,
} from "../../constants/interfaces";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import "./styles.scss";

interface IFiltersProps {
  autocompleteFilterProps?: IAutocompleteFilterProps;
  searchFilterProps?: ISearchFilterConfig;
  calendarProps?: ICalendarProps;
  buttonsProps?: IButtonProps[];
  downloadCSVButtonProps?: IButtonProps;
  csvDataForDownload?: any[];
  onDownloadCSV?: () => void;
  idCalendar?: string;
}

const Filters = observer((props: IFiltersProps) => {
  const {
    autocompleteFilterProps,
    searchFilterProps,
    calendarProps,
    buttonsProps,
    downloadCSVButtonProps,
    onDownloadCSV,
  } = props;
  const defaultButtonSxStyle = {
    padding: "0.492rem 2.1rem",
    textTransform: "capitalize",
    fontSize: "1.5rem",
    borderRadius: ".8rem",
    "& .MuiButton-icon": {
      "& > *:nth-of-type(1)": {
        fontSize: "1.5rem",
      },
    },
  };

  //#region State
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [startDateError, setStartDateError] = useState<string>("");
  const [endDateError, setEndDateError] = useState<string>("");

  const [singleDate, setSingleDate] = useState<DateTime | null>(null);
  const [singleDateError, setSingleDateError] = useState<string>("");
  //#endregion

  useEffect(() => {
    return () => {
      setStartDateError("");
      setEndDateError("");
      setStartDate(null);
      setEndDate(null);
      setSingleDate(null);
      setSingleDateError("");
    };
  }, []);

  //#region Autocomplete logic
  const handleAutocompleteMultiChange = (event: React.ChangeEvent<{}>, value: IOption[] | IOption | null) => {
    const onChange = autocompleteFilterProps?.OnChangeMulti;
    if (onChange) {
      onChange(value);
    }
  };

  const handleAutocompleteSingleChange = (event: React.ChangeEvent<{}>, value: IOption[] | IOption | null) => {
    const onChange = autocompleteFilterProps?.onChange;
    if (onChange) {
      onChange(value);
    }
  };

  const handleAutocompleteSecondChange = (event: React.ChangeEvent<{}>, value: IOption[] | IOption | null) => {
    const onChange = autocompleteFilterProps?.onChangeSecond;
    if (onChange) {
      onChange(value);
    }
  };
  //#endregion Autocomplete

  //#region Calendar logic
  useEffect(() => {
    if (calendarProps?.withCalendarRange) {
      calendarProps?.startDate && setStartDate(calendarProps?.startDate);
      calendarProps?.endDate && setEndDate(calendarProps?.endDate);
    }

    if (calendarProps?.withSingleCalendar) {
      calendarProps?.singleDate && setSingleDate(calendarProps?.singleDate);
    }

    return () => {
      //Calendar clear
      setStartDateError("");
      setEndDateError("");
      setStartDate(null);
      setEndDate(null);
      setSingleDate(null);
      setSingleDateError("");
    };
  }, [calendarProps?.withCalendarRange, calendarProps?.withSingleCalendar]);

  const handleDateFromChange = (newValue: DateTime | null, error: any) => {
    if (error?.validationError) setStartDateError("Invalid date");
    else {
      setStartDate(newValue);
      setStartDateError("");
    }
  };

  const handleDateToChange = (newValue: DateTime | null, error: any) => {
    if (error?.validationError) setEndDateError("Invalid date");
    else {
      setEndDate(newValue);
      setEndDateError("");
    }
  };

  const handleSingleDateChange = (newValue: DateTime | null, error: any) => {
    if (error?.validationError) {
      setSingleDateError("Invalid date");
    } else {
      setSingleDate(newValue);
      setSingleDateError("");
      calendarProps?.onSingleDateChange?.(newValue);
    }
  };

  const dateValidator = () => {
    if (startDate === null && endDate === null) {
      setEndDateError("");
      const customOnChange = calendarProps?.customOnChange;
      if (customOnChange) {
        customOnChange(undefined, undefined);
      }
    }
    if (startDate !== null && endDate !== null) {
      if (endDate.startOf("day") < startDate.startOf("day")) {
        setEndDateError("Date must be greater than start date");
        setEndDate(null);
      } else {
        setEndDateError("");
        const customOnChange = calendarProps?.customOnChange;
        if (customOnChange) {
          const startDateAux = startDate.toISODate()?.toString() ?? undefined;
          const endDateAux = endDate.toISODate()?.toString() ?? undefined;
          customOnChange(startDateAux, endDateAux);
        }
      }
    }
  };

  useEffect(() => {
    dateValidator();
  }, [startDate, endDate]);

  //#endregion Calendar logic

  return (
    <div className="filters">
      {autocompleteFilterProps?.withInputAutocompleteSingle && autocompleteFilterProps?.optionsSingle && (
        <div className="filter-item">
          <InputAutocomplete
            options={autocompleteFilterProps.optionsSingle}
            label={autocompleteFilterProps.labelSingle}
            value={autocompleteFilterProps.singleValue}
            placeholder={autocompleteFilterProps.placeholderSingle}
            icon={autocompleteFilterProps.iconSingle}
            iconPosition={autocompleteFilterProps.iconPositionSingle}
            sx={autocompleteFilterProps.sxSingle}
            multiple={false}
            onChange={handleAutocompleteSingleChange}
            id={autocompleteFilterProps.idSingle}
          />
        </div>
      )}
      {autocompleteFilterProps?.withInputAutocompleteSecond && autocompleteFilterProps?.optionsSecond && (
        <div className="filter-item">
          <InputAutocomplete
            options={autocompleteFilterProps.optionsSecond}
            label={autocompleteFilterProps.labelSecond}
            value={autocompleteFilterProps.singleValueSecond}
            placeholder={autocompleteFilterProps.placeholderSecond}
            icon={autocompleteFilterProps.iconSecond}
            iconPosition={autocompleteFilterProps.iconPositionSecond}
            sx={autocompleteFilterProps.sxSecond}
            multiple={false}
            onChange={handleAutocompleteSecondChange}
            id={autocompleteFilterProps.idSecond}
          />
        </div>
      )}
      {autocompleteFilterProps?.withInputAutocompleteMulti && autocompleteFilterProps?.optionsMulti && (
        <div className="filter-item">
          <InputAutocomplete
            options={autocompleteFilterProps.optionsMulti}
            label={autocompleteFilterProps.labelMulti}
            value={autocompleteFilterProps.multiValue}
            placeholder={autocompleteFilterProps.placeholderMulti}
            icon={autocompleteFilterProps.iconMulti}
            iconPosition={autocompleteFilterProps.iconPositionMulti}
            sx={autocompleteFilterProps.sxMulti}
            multiple={true}
            onChange={handleAutocompleteMultiChange}
            id={autocompleteFilterProps.idMulti}
          />
        </div>
      )}
      {searchFilterProps?.show && (
        <div className="filter-item">
          <SearchFilter
            id={searchFilterProps.id}
            label={searchFilterProps.label}
            placeholder={searchFilterProps.placeholder}
            sx={searchFilterProps.sx}
            initialValue={searchFilterProps.initialValue}
            minChars={searchFilterProps.minChars}
            debounceMs={searchFilterProps.debounceMs}
            onAction={searchFilterProps.onAction}
            onChange={searchFilterProps.onChange}
          />
        </div>
      )}
      {calendarProps?.withCalendarRange && (
        <div className="filter-item filter-calendar-range filters-calendar-separator" id={props.idCalendar}>
          <Calendar
            value={calendarProps.startDate}
            maxDate={calendarProps.allowFuture ? undefined : DateTime.now()}
            label="Start date"
            sx={calendarProps.sx}
            error={startDateError}
            onChange={handleDateFromChange}
          />
          <hr />
          <Calendar
            value={calendarProps.endDate}
            maxDate={calendarProps.allowFuture ? undefined : DateTime.now()}
            label="End date"
            sx={calendarProps.sx}
            error={endDateError}
            onChange={handleDateToChange}
          />
        </div>
      )}
      {calendarProps?.withSingleCalendar && (
        <div className="filter-item filter-calendar-single filters-calendar-separator" id={props.idCalendar}>
          {calendarProps?.singleTitle && <span>{calendarProps?.singleTitle}</span>}
          <Calendar
            value={calendarProps.singleDate}
            maxDate={calendarProps?.maxDate ?? (calendarProps?.allowFuture ? undefined : DateTime.now())}
            label={calendarProps?.singleLabel ?? "Select a date"}
            sx={calendarProps?.sx}
            error={singleDateError}
            onChange={handleSingleDateChange}
          />
        </div>
      )}
      {buttonsProps?.map((button) => (
        <div className={button.className ? button.className + " filter-item-btn" : "filter-item-btn"}>
          <Button
            id={button.id}
            onClick={button.onClick}
            text={button.text}
            sx={defaultButtonSxStyle}
            startIcon={button.startIcon}
            disabled={button.disabled}
            variant={button.variant}
            size={button.size}
          />
        </div>
      ))}

      {downloadCSVButtonProps?.show && (
        <div className="filter-item-btn filter-CSV-btn">
          <Button
            id={downloadCSVButtonProps.id}
            onClick={onDownloadCSV}
            text={downloadCSVButtonProps.text}
            sx={defaultButtonSxStyle}
            startIcon={downloadCSVButtonProps.startIcon}
            disabled={downloadCSVButtonProps.disabled}
            variant={downloadCSVButtonProps.variant}
            size={downloadCSVButtonProps.size}
          />
        </div>
      )}
    </div>
  );
});

export default Filters;
